import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { 
  Search, Plus, X, Save, Maximize2, Minimize2, FileText,
  Clock, Calendar, Trash2, Share2, Check, Sparkles, RotateCcw
} from 'lucide-react';

const NotesPage = () => {
  const [notes, setNotes] = useState(() => {
    const saved = localStorage.getItem('my-local-notes');
    return saved ? JSON.parse(saved) : [];
  });
  
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [noteForm, setNoteForm] = useState({ title: '', content: '' });
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    localStorage.setItem('my-local-notes', JSON.stringify(notes));
  }, [notes]);

  // --- Fixed Share Link Logic ---
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sharedData = params.get('view');
    if (sharedData) {
      try {
        // Robust decoding for UTF-8 and special characters
        const decodedString = decodeURIComponent(
          atob(sharedData.replace(/-/g, '+').replace(/_/g, '/'))
            .split('')
            .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
        );
        const decodedNote = JSON.parse(decodedString);
        
        setNoteForm({ 
            title: `✨ ${decodedNote.title}` || 'Shared Note', 
            content: decodedNote.content || '' 
        });
        setEditingId(null);
        setIsModalOpen(true);
        // Clean URL
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (e) { 
        console.error("Shared link decoding failed", e); 
      }
    }
  }, []);

  const handleSave = () => {
    if (!noteForm.title.trim() && !noteForm.content.trim()) {
      setIsModalOpen(false);
      return;
    }
    
    const timestamp = {
      date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    if (editingId) {
      setNotes(notes.map(n => n.id === editingId ? { ...n, ...noteForm, ...timestamp } : n));
    } else {
      setNotes([{ ...noteForm, id: Date.now(), ...timestamp }, ...notes]);
    }

    setNoteForm({ title: '', content: '' });
    setIsModalOpen(false);
    setIsFullScreen(false);
    setEditingId(null);
  };

  const handleShare = (e, note) => {
    e.stopPropagation();
    try {
      const data = JSON.stringify({ title: note.title, content: note.content });
      // Robust encoding for UTF-8 (emojis/special chars)
      const encoded = btoa(encodeURIComponent(data).replace(/%([0-9A-F]{2})/g, (match, p1) => 
        String.fromCharCode('0x' + p1)
      ))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');

      const url = `${window.location.origin}${window.location.pathname}?view=${encoded}`;
      
      navigator.clipboard.writeText(url).then(() => {
        setCopiedId(note.id);
        setTimeout(() => setCopiedId(null), 2000);
      });
    } catch (err) {
      console.error("Encoding failed", err);
    }
  };

  const deleteNote = (e, id) => {
    e.stopPropagation();
    setNotes(notes.filter(n => n.id !== id));
  };

  const filteredNotes = notes.filter(n => 
    n.title?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    n.content?.replace(/<[^>]*>/g, '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="app-wrapper">
      <div className="notes-container">
        <header className="notes-header">
          <div className="brand-section">
            <div className="logo-box"><Sparkles size={24} /></div>
            <div>
              <h1> MyNotes </h1>
              <p>{filteredNotes.length} documents total</p>
            </div>
          </div>
          <button className="create-btn" onClick={() => { setEditingId(null); setNoteForm({title:'', content:''}); setIsModalOpen(true); }}>
            <Plus size={20} />
            <span>Add</span>
          </button>
        </header>

        <div className="search-container">
          <div className="search-bar">
            <Search size={18} className="search-icon" />
            <input 
              type="text" 
              placeholder="Search library..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          {searchTerm && (
            <button className="search-cancel" onClick={() => setSearchTerm('')}>
              <RotateCcw size={16} />
              <span>Clear Search</span>
            </button>
          )}
        </div>

        {filteredNotes.length > 0 ? (
          <div className="notes-grid">
            {filteredNotes.map((note) => (
              <div key={note.id} className="glass-card" onClick={() => { setEditingId(note.id); setNoteForm({title: note.title, content: note.content}); setIsModalOpen(true); }}>
                <div className="card-top">
                  <h3>{note.title || 'Untitled'}</h3>
                  <div className="card-actions">
                    <button onClick={(e) => handleShare(e, note)} className={`mini-btn ${copiedId === note.id ? 'success' : ''}`}>
                      {copiedId === note.id ? <Check size={14}/> : <Share2 size={14} />}
                    </button>
                    <button onClick={(e) => deleteNote(e, note.id)} className="mini-btn delete">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <div className="card-preview" dangerouslySetInnerHTML={{ __html: note.content }} />
                <div className="card-footer">
                  <div className="meta-item"><Calendar size={12}/> {note.date}</div>
                  <div className="meta-item"><Clock size={12}/> {note.time}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <FileText size={48} />
            <h3>No results found</h3>
            <p>Try adjusting your search or create a new entry.</p>
          </div>
        )}

        {isModalOpen && (
          <div className="modal-overlay">
            <div className={`modal-window ${isFullScreen ? 'full' : ''}`}>
              <nav className="modal-nav">
                <div className="nav-group">
                  <button className="round-btn close" onClick={() => setIsModalOpen(false)}><X size={18}/></button>
                  <button className="round-btn" onClick={() => setIsFullScreen(!isFullScreen)}>
                    {isFullScreen ? <Minimize2 size={16}/> : <Maximize2 size={16}/>}
                  </button>
                </div>
                <button className="save-btn" onClick={handleSave}>
                  <Save size={18} />
                  <span>{editingId ? 'Update' : 'Save'}</span>
                </button>
              </nav>
              <div className="editor-body">
                <input 
                  className="editor-title"
                  placeholder="Enter Title..."
                  value={noteForm.title}
                  onChange={(e) => setNoteForm({...noteForm, title: e.target.value})}
                />
                <div className="quill-container">
                  <ReactQuill theme="snow" value={noteForm.content} onChange={(val) => setNoteForm({...noteForm, content: val})} placeholder="Start typing..."/>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');
        :root {
          --primary: #6366f1;
          --primary-hover: #4f46e5;
          --bg: #f8fafc;
          --text: #1e293b;
          --text-muted: #64748b;
          --border: #e2e8f0;
        }

        .app-wrapper { background: var(--bg); min-height: 100vh; font-family: 'Plus Jakarta Sans', sans-serif; color: var(--text); padding: 40px 20px; }
        .notes-container { max-width: 1200px; margin: 0 auto; }
        
        .notes-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 40px; }
        .brand-section { display: flex; align-items: center; gap: 16px; }
        .logo-box { background: var(--primary); color: white; padding: 10px; border-radius: 12px; box-shadow: 0 8px 16px rgba(99, 102, 241, 0.2); }
        .brand-section h1 { font-size: 24px; font-weight: 700; margin: 0; }
        
        .create-btn { background: var(--primary); color: white; border: none; padding: 12px 24px; border-radius: 14px; display: flex; align-items: center; gap: 10px; font-weight: 600; cursor: pointer; transition: 0.3s; }
        .create-btn:hover { background: var(--primary-hover); transform: translateY(-2px); }

        .search-container { display: flex; align-items: center; gap: 12px; margin-bottom: 40px; }
        .search-bar { background: white; border: 1px solid var(--border); padding: 12px 20px; border-radius: 16px; display: flex; align-items: center; gap: 12px; flex: 1; transition: 0.2s; }
        .search-bar:focus-within { border-color: var(--primary); box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1); }
        .search-bar input { border: none; outline: none; width: 100%; font-size: 16px; font-family: inherit; }
        .search-cancel { display: flex; align-items: center; gap: 8px; background: #f1f5f9; border: none; padding: 12px 18px; border-radius: 14px; color: var(--text-muted); font-weight: 600; cursor: pointer; transition: 0.2s; white-space: nowrap; }
        .search-cancel:hover { background: #e2e8f0; color: var(--text); }

        .notes-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 24px; }
        .glass-card { background: white; border: 1px solid var(--border); border-radius: 24px; padding: 24px; cursor: pointer; transition: 0.3s; height: 220px; display: flex; flex-direction: column; }
        .glass-card:hover { transform: translateY(-5px); border-color: var(--primary); box-shadow: 0 12px 24px rgba(0,0,0,0.05); }
        
        .card-top { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 12px; }
        .card-top h3 { margin: 0; font-size: 18px; font-weight: 700; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 180px; }
        .card-actions { display: flex; gap: 6px; }
        .mini-btn { background: #f8fafc; border: 1px solid var(--border); padding: 8px; border-radius: 10px; cursor: pointer; color: var(--text-muted); transition: 0.2s; }
        .mini-btn:hover { background: white; color: var(--primary); border-color: var(--primary); }
        .mini-btn.success { background: #10b981; color: white; border-color: #10b981; }

        .card-preview { font-size: 14px; color: var(--text-muted); flex: 1; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; line-height: 1.6; }
        .card-footer { display: flex; gap: 16px; padding-top: 16px; border-top: 1px solid #f8fafc; margin-top: auto; }
        .meta-item { display: flex; align-items: center; gap: 6px; font-size: 12px; color: var(--text-muted); font-weight: 500; }

        .empty-state { text-align: center; padding: 80px 0; color: var(--text-muted); }
        .empty-state h3 { color: var(--text); margin-top: 16px; }

        .modal-overlay { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.4); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 1000; }
        .modal-window { background: white; width: 90%; max-width: 1000px; height: 85vh; border-radius: 32px; display: flex; flex-direction: column; box-shadow: 0 40px 80px rgba(0,0,0,0.25); }
        .modal-window.full { width: 100%; height: 100%; border-radius: 0; }
        .modal-nav { padding: 20px 30px; border-bottom: 1px solid #f1f5f9; display: flex; justify-content: space-between; align-items: center; }
        .round-btn { border: 1px solid #f1f5f9; background: white; padding: 10px; border-radius: 50%; cursor: pointer; color: var(--text-muted); }
        .save-btn { background: #1e293b; color: white; border: none; padding: 10px 24px; border-radius: 14px; font-weight: 600; display: flex; align-items: center; gap: 8px; cursor: pointer; }
        .editor-body { flex: 1; padding: 40px 60px; overflow-y: auto; }
        .editor-title { width: 100%; font-size: 36px; font-weight: 800; border: none; outline: none; margin-bottom: 24px; font-family: inherit; }
        .quill-container { height: calc(100% - 80px); }
        .ql-container.ql-snow { border: none !important; font-size: 18px; }
        .ql-toolbar.ql-snow { border: none !important; border-bottom: 1px solid #f1f5f9 !important; margin-bottom: 10px; }
      `}</style>
    </div>
  );
};

export default NotesPage;